import { useLocalStorageState, useMemoizedFn } from "ahooks";
import { type Options } from "ahooks/lib/createUseStorageState";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";

/**
 * 关闭xxx模块，当天不再展示，通过localStorage展示
 * @param key localStorage的key
 * @param options useLocalStorageState的配置信息
 * @returns [closeState, onClose] 关闭状态， 设置关闭方法
 * @example const [closeState, onClose] = useCloseToday("xxx_close_state");
 * // 渲染内容
 * !closeState && (<>xxxxx<button onClick={onClose}>关闭按钮</button></>)
 */
export function useCloseToday(
  key: string,
  options?: Options<{
    state: boolean;
    time?: number;
  }>
) {
  const [value, setValue] = useLocalStorageState(key, {
    defaultValue: {
      state: false,
    },
    ...(options || null),
  });

  const closeState = useMemo(() => {
    return !!(value.state && value.time >= Date.now());
  }, [value.state, value.time]);

  const onClose = useCallback(() => {
    setValue({
      state: true,
      // 过期时间,当天有效
      time: dayjs().endOf("date").valueOf(),
    });
  }, [setValue]);

  return [closeState, onClose] as [boolean, () => void];
}

export function useTodayLocalState<T>(
  key: string,
  options?: Options<{
    value: T;
    time?: number;
  }>
) {
  const getDefaultValue = useMemoizedFn(() => {
    if (options?.defaultValue instanceof Function) {
      return options?.defaultValue();
    }
    return (
      options?.defaultValue || {
        value: null,
      }
    );
  });

  const [state, setState] = useLocalStorageState(key, {
    ...(options || null),
  });

  const value = useMemo(() => {
    if (state.time && state.time >= Date.now()) {
      return state.value;
    }
    return getDefaultValue().value;
  }, [state, getDefaultValue]);

  const setValue = useMemoizedFn(
    (newValue: T | ((prevValue: T | undefined) => T)) => {
      setState((prevState) => {
        const nextValue =
          typeof newValue === "function"
            ? (newValue as (prevValue: T | undefined) => T)(prevState?.value)
            : newValue;

        return {
          value: nextValue,
          time: dayjs().endOf("date").valueOf(),
        };
      });
    }
  );

  return [value, setValue] as const;
}
